import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { format } from 'date-fns';
import { of, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { DictionaryService } from '../dictionary/dictionary.service';
import { DictionaryGroup } from '../dictionary/types/dictionary-groups.enum';
import { DictionaryEntry } from '../dictionary/types/dictionary-list.dto';
import { UTCDateAdapter } from '../shared/adapters/utc-date.adapter';
import { Resources } from '../shared/enums/resources.enum';
import { removeNullOrUndefined, removeUndefined } from '../shared/helper-functions';
import { AuthService } from '../shared/services/auth.service';
import { ChaosTableService } from './chaos-table.service';
import { JoinRowsDialogComponent } from './join-rows-dialog/join-rows-dialog.component';
import { FilterType } from './types/chaos-table-list.dto';
import { ChaosTableRow, LanguageEnum } from './types/chaos-table-row.interface';
import { ChaosTableStatus, PaymentMethod, Source } from './types/chaos-table.enum';
import { UploadDialogComponent } from './upload-dialog/upload-dialog.component';
import { EmailTemplateSendComponent } from './email-template-send/email-template-send.component';
import { ChaosTableStudentStatus } from '../admin/admin.status';

export const PAYMENT_PROBLEM = 'PAYMENT_PROBLEM';

@Component({
   selector: 'app-chaos-table',
   templateUrl: './chaos-table.component.html',
   styleUrls: ['./chaos-table.component.css'],
   providers: [{ provide: DateAdapter, useClass: UTCDateAdapter }]
})
export class ChaosTableComponent implements OnInit, OnDestroy {
   dataSource = new MatTableDataSource<ChaosTableRow>();

   destroyed$ = new Subject<void>();

   areas: DictionaryEntry[] = [];

   Resources = Resources;

   firstHeaderRow = [
      'id',
      'name',
      'birthDate',
      'taxNumber',
      'area',
      'startDate',
      'rowStatus',
      'studentStatus',
      'approvedAt',
      'paymentMethod',
      'syncDate',
      'comment',
      'createdAt',
      'op'
   ];

   secondHeaderRow = [
      'createdBy',
      'phone',
      'email',
      'project',
      'language',
      'loginLinkDate',
      'reminderDate',
      'approvedBy',
      'dataSupplementEmailDate',
      'check'
   ];

   readonly languages = [LanguageEnum.HU, LanguageEnum.EN];

   readonly paymentMethods = Object.keys(PaymentMethod).filter(k => isNaN(Number(k)));

   readonly studentStatuses = [
      ChaosTableStudentStatus.APPLICATION_IN_PROGRESS,
      ChaosTableStudentStatus.NONE,
      PAYMENT_PROBLEM,
      ChaosTableStudentStatus.NEW_APPLICANT,
      ChaosTableStudentStatus.REVISED_WAITING_FOR_SYNC,
      ChaosTableStudentStatus.NEEDS_CORRECTION,
      ChaosTableStudentStatus.SYNCED,
      ChaosTableStudentStatus.ALREADY_MEMBER,
      ChaosTableStudentStatus.TRASH,
      ChaosTableStudentStatus.EXPIRED_LINK
   ];

   readonly rowStatuses = Object.keys(ChaosTableStatus).filter(k => isNaN(Number(k)));

   rowEditInProgress: ChaosTableRow | undefined = undefined;

   rowAddInProgress: ChaosTableRow | undefined = undefined;

   totalRows = 0;

   pageSize = 25;

   pageIndex = 0;

   canEditRow = false;

   searchForm: FormGroup;

   filterForm: FormGroup;

   format = format;

   dialogRef: MatDialogRef<EmailTemplateSendComponent> | null = null;

   getStatus = getStatus;

   constructor(
      private chaosTableService: ChaosTableService,
      private authService: AuthService,
      private snackBar: MatSnackBar,
      public dialog: MatDialog,
      private router: Router,
      private dictService: DictionaryService
   ) {
      this.canEditRow = [
         Resources.CTAreaEdit,
         Resources.CTCheckEdit,
         Resources.CTCommentEdit,
         Resources.CTProjectEdit,
         Resources.CTLanguageEdit,
         Resources.CTEducationStartEdit
      ].some(r => this.hasResource(r));

      this.searchForm = new FormGroup({
         searchField: new FormControl()
      });

      this.filterForm = new FormGroup({
         id: new FormControl(),
         name: new FormControl(),
         birthDate: new FormControl(),
         taxNumber: new FormControl(),
         area: new FormControl(),
         startDateFrom: new FormControl(),
         startDateTo: new FormControl(),
         rowStatus: new FormControl(),
         studentStatus: new FormControl(),
         approvedAtFrom: new FormControl(),
         approvedAtTo: new FormControl(),
         paymentMethod: new FormControl(),
         syncDateFrom: new FormControl(),
         syncDateTo: new FormControl(),
         comment: new FormControl(),
         createdAtFrom: new FormControl(),
         createdAtTo: new FormControl(),
         createdBy: new FormControl(),
         phone: new FormControl(),
         email: new FormControl(),
         project: new FormControl(),
         language: new FormControl(),
         loginLinkDateFrom: new FormControl(),
         loginLinkDateTo: new FormControl(),
         reminderDateFrom: new FormControl(),
         reminderDateTo: new FormControl(),
         approvedBy: new FormControl(),
         dataSupplementEmailDateFrom: new FormControl(),
         dataSupplementEmailDateTo: new FormControl(),
         check: new FormControl()
      });

      this.searchForm.controls.searchField.valueChanges
         .pipe(takeUntil(this.destroyed$), debounceTime(1000))
         .subscribe(searchTerm => {
            if (typeof searchTerm === 'string') {
               this.pageIndex = 0;
               return this.loadPage(searchTerm, this.getFilter());
            }
            return of([]);
         });

      this.dictService.list({ group: DictionaryGroup.AREA }).subscribe(res => {
         this.areas = res;
      });
   }

   ngOnInit(): void {
      this.loadPage();
   }

   loadPage(search?: string, filter?: FilterType) {
      this.chaosTableService
         .list({
            page: this.pageIndex,
            pageSize: this.pageSize,
            search,
            filter
         })
         .subscribe(
            res => {
               this.dataSource.data = res.list;
               this.totalRows = res.count;
            },
            error => {
               this.handleError(error);
            }
         );
   }

   sendEnrollmentLink(data: { id: number; sender?: string; comment?: string }) {
      this.chaosTableService.sendEnrollmentLink(data).subscribe(
         res => {
            if (res.ok) {
               if (res.body) {
                  let index = this.dataSource.data.findIndex(e => e.id === res.body?.id);
                  this.dataSource.data[index] = { ...this.dataSource.data[index], ...res.body };
                  this.dataSource._updateChangeSubscription();
               }
               this.snackBar.open(`A beiratkozó email sikeresen elküldve!`, 'Ok', {
                  horizontalPosition: 'center',
                  verticalPosition: 'top',
                  duration: 5000
               });
            } else {
               this.handleError(res.statusText);
            }
         },
         error => {
            this.handleError(error);
         }
      );
   }

   sendReminderLink(data: { id: number; comment?: string }) {
      this.chaosTableService.sendReminderLink(data).subscribe(
         res => {
            if (res.ok) {
               if (res.body) {
                  let index = this.dataSource.data.findIndex(e => e.id === res.body?.id);
                  this.dataSource.data[index] = { ...this.dataSource.data[index], ...res.body };
                  this.dataSource._updateChangeSubscription();
               }
               this.snackBar.open(`Az emlékeztető email sikeresen elküldve!`, 'Ok', {
                  horizontalPosition: 'center',
                  verticalPosition: 'top',
                  duration: 5000
               });
            } else {
               this.handleError(res.statusText);
            }
         },
         error => {
            this.handleError(error);
         }
      );
   }

   closeRow(id: number) {
      this.chaosTableService.closeRow({ id }).subscribe(
         res => {
            if (res.ok) {
               this.snackBar.open(`A Sor sikeresen le lett zárva!`, 'Ok', {
                  horizontalPosition: 'center',
                  verticalPosition: 'top',
                  duration: 5000
               });
               this.loadPage(this.searchForm.controls.searchField.value, this.getFilter());
            } else {
               this.handleError(res.statusText);
            }
         },
         error => {
            this.handleError(error);
         }
      );
   }

   hasResource(resource: string): boolean {
      return this.authService.hasResource(resource);
   }

   onEdit(row: ChaosTableRow) {
      if (this.rowAddInProgress) return;
      if (this.rowEditInProgress && this.rowEditInProgress.id === row.id) return;
      if (!this.canEditRow) {
         this.snackBar.open(`Nincs szerkesztési jogosultsága!`, 'Forbidden', {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            duration: 5000
         });
         return;
      }

      this.rowEditInProgress = { ...row };
   }

   onCancel() {
      this.rowEditInProgress = undefined;
      if (this.rowAddInProgress) {
         this.dataSource.data.shift();
         this.dataSource.data = [...this.dataSource.data];
         this.rowAddInProgress = undefined;
      }
   }

   onSaveEdit() {
      if (!this.rowEditInProgress) return;
      this.chaosTableService
         .editRow({
            id: this.rowEditInProgress.id,
            area: this.rowEditInProgress.area,
            check: this.rowEditInProgress.check,
            comment: this.rowEditInProgress.comment,
            project: this.rowEditInProgress.project,
            startDate: this.rowEditInProgress.startDate
               ? format(new Date(this.rowEditInProgress.startDate), 'yyyy-MM-dd')
               : undefined,
            language: this.rowEditInProgress.language
         })
         .subscribe(
            () => {
               this.snackBar.open(`A Sor sikeresen módosítva!`, 'Ok', {
                  horizontalPosition: 'center',
                  verticalPosition: 'top',
                  duration: 5000
               });
               if (this.rowEditInProgress) {
                  const modifiedItem = this.dataSource.data.find(
                     r => r.id === this.rowEditInProgress?.id
                  );
                  if (modifiedItem) {
                     modifiedItem.area = this.rowEditInProgress.area;
                     modifiedItem.check = this.rowEditInProgress.check;
                     modifiedItem.comment = this.rowEditInProgress.comment;
                     modifiedItem.project = this.rowEditInProgress.project;
                     modifiedItem.startDate = this.rowEditInProgress.startDate;
                     modifiedItem.language = this.rowEditInProgress.language;
                  }
                  this.rowEditInProgress = undefined;
               }
            },
            error => {
               this.handleError(error);
            }
         );
   }

   canBeSaved() {
      if (this.rowEditInProgress) {
         return true;
      }

      if (this.rowAddInProgress) {
         return (
            this.rowAddInProgress.area &&
            this.rowAddInProgress.area.trim() != '' &&
            this.rowAddInProgress.name &&
            this.rowAddInProgress.name.trim() != '' &&
            this.rowAddInProgress.birthDate &&
            this.rowAddInProgress.email &&
            this.rowAddInProgress.email.trim() != '' &&
            this.rowAddInProgress.phone &&
            this.rowAddInProgress.phone.trim() != '' &&
            this.rowAddInProgress?.project &&
            this.rowAddInProgress.project.trim() != '' &&
            this.rowAddInProgress.language
         );
      }
      return false;
   }

   onSave() {
      if (this.rowEditInProgress) this.onSaveEdit();
      if (this.rowAddInProgress) this.onSaveNewRow();
   }

   onSaveNewRow() {
      if (!this.rowAddInProgress) return;
      this.chaosTableService
         .addNewRow({
            area: this.rowAddInProgress.area,
            birthDate: this.rowAddInProgress.birthDate
               ? format(this.rowAddInProgress.birthDate, 'yyyy-MM-dd')
               : '',
            email: this.rowAddInProgress.email,
            name: this.rowAddInProgress.name,
            phone: this.rowAddInProgress.phone,
            project: this.rowAddInProgress.project,
            language: this.rowAddInProgress.language
         })
         .subscribe(
            () => {
               this.rowAddInProgress = undefined;
               this.snackBar.open(`Új sor sikeresen rögzítve!`, 'Ok', {
                  horizontalPosition: 'center',
                  verticalPosition: 'top',
                  duration: 5000
               });
               this.loadPage(this.searchForm.controls.searchField.value, this.getFilter());
            },
            error => {
               this.handleError(error);
            }
         );
   }

   onAddColumn() {
      this.rowEditInProgress = undefined;
      this.rowAddInProgress = {
         id: 0,
         area: '',
         name: '',
         phone: '',
         email: '',
         project: '',
         source: Source.MANUAL
      };
      this.dataSource.data = [this.rowAddInProgress, ...this.dataSource.data];
   }

   onPageChange(event: PageEvent) {
      this.pageSize = event.pageSize;
      this.pageIndex = event.pageIndex;
      this.loadPage(this.searchForm.controls.searchField.value, this.getFilter());
   }

   handleError(error: any) {
      let errText: string;

      if (error?.error?.message) {
         errText = error.error.message;
      } else if (error?.message) {
         errText = error.message;
      } else {
         errText = error.toString();
      }

      this.snackBar.open(errText, 'Error', {
         horizontalPosition: 'center',
         verticalPosition: 'top',
         duration: 5000
      });
   }

   convertDateStringToDate(dateString: string): Date | null {
      const normalized = dateString.replace(/[.-]/g, '');
      const match = normalized.match(/^(\d{4})(\d{2})(\d{2})$/);
      if (match) {
         const year = +match[1];
         const month = +match[2] - 1; // JS month is 0-based
         const day = +match[3];
         return new Date(Date.UTC(year, month, day));
      }
      return null;
   }

   onRawDateInput<T>(event: Event, targetRow: T | undefined, field: keyof T) {
      if (!targetRow) return;

      const target = event.target as HTMLInputElement;
      const input = target.value;

      const parsed = this.convertDateStringToDate(input);
      if (parsed && targetRow) {
         (targetRow as any)[field] = parsed;
      }
   }

   onParsedFilterDateInput(event: any, field: string) {
      const input = event.target.value;

      const parsed = this.convertDateStringToDate(input);
      if (parsed) {
         this.filterForm.controls[field].setValue(parsed);
      }
   }

   //Basically an overwrite
   onJoinRow(row: ChaosTableRow) {
      const dialogRef = this.dialog.open(JoinRowsDialogComponent, {
         maxWidth: '50vw',
         width: '40vw',
         height: '50vh',
         maxHeight: '80vh',
         data: row,
         disableClose: true
      });

      dialogRef.afterClosed().subscribe(value => {
         // console.log(value);
         if (value) {
            this.loadPage(this.searchForm.controls.searchField.value, this.getFilter());
         }
      });
   }

   onCopy(row: ChaosTableRow) {
      const dataToCopy = `${row.name}\n${row.birthDate}\n${row.taxNumber ?? ''}\n${row.phone}\n${
         row.email
      }`;
      navigator.clipboard
         .writeText(dataToCopy)
         .then()
         .catch(() => console.error('Could not copy data to clipboard'));
   }

   navigateToHistory(rowId?: number) {
      this.router.navigate(['chaos-table/history'], {
         queryParams: removeUndefined({
            rowId: rowId
         })
      });
   }

   openDialog() {
      const dialogRef = this.dialog.open(UploadDialogComponent, {
         width: '400px'
      });

      dialogRef.afterClosed().subscribe((file: File | null) => {
         if (file) {
            this.uploadFile(file);
         }
      });
   }

   uploadFile(file: File) {
      this.chaosTableService.uploadXLSX(file).subscribe(
         () => {
            this.snackBar.open(`Fájl sikeres feltöltve!`, 'Ok', {
               horizontalPosition: 'center',
               verticalPosition: 'top',
               duration: 5000
            });
            this.loadPage(this.searchForm.controls.searchField.value, this.getFilter());
         },
         error => {
            this.handleError(error);
         }
      );
   }

   private normalizeDateFilters(filter: any): FilterType {
      const dateFields: (keyof FilterType)[] = [
         'birthDate',
         'createdAtFrom',
         'createdAtTo',
         'startDateFrom',
         'startDateTo',
         'approvedAtFrom',
         'approvedAtTo',
         'syncDateFrom',
         'syncDateTo',
         'loginLinkDateFrom',
         'loginLinkDateTo',
         'reminderDateFrom',
         'reminderDateTo',
         'dataSupplementEmailDateFrom',
         'dataSupplementEmailDateTo'
      ];

      dateFields.forEach(field => {
         const value = filter[field];
         if (value instanceof Date) {
            filter[field] = format(value, 'yyyy-MM-dd');
         }
      });

      return filter as FilterType;
   }

   private getFilter(): FilterType {
      const filter: any = this.normalizeDateFilters(removeNullOrUndefined(this.filterForm.value));

      if (filter.studentStatus) {
         if (filter.studentStatus === PAYMENT_PROBLEM) {
            filter.studentStatus = [
               ChaosTableStudentStatus.REGISTERED,
               ChaosTableStudentStatus.WAITING_FOR_PAYMENT,
               ChaosTableStudentStatus.WAITING_FOR_IPN
            ];
         } else {
            filter.studentStatus = [filter.studentStatus];
         }
      }
      return filter;
   }

   onFilter() {
      this.pageIndex = 0;
      this.loadPage(this.searchForm.controls.searchField.value, this.getFilter());
   }

   onClearFilter() {
      this.filterForm.reset();
      this.pageIndex = 0;
      this.loadPage();
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
      this.destroyed$.complete();
   }

   sendEnrollmentLinkDisabled(row: ChaosTableRow) {
      return (
         !this.hasResource(Resources.CTSendEnrollment) ||
         row.deleted ||
         !row.language ||
         !row.area ||
         !row.birthDate ||
         !row.email ||
         !row.name ||
         !row.phone ||
         !row.project ||
         !!row.loginLinkDate
      );
   }

   sendReminderEmailDisabled(row: ChaosTableRow) {
      return (
         !this.hasResource(Resources.CTSendReminder) ||
         row.deleted ||
         !row.language ||
         !row.area ||
         !row.birthDate ||
         !row.email ||
         !row.name ||
         !row.phone ||
         !row.project ||
         !row.loginLinkDate
      );
   }

   closeRowDisabled(row: ChaosTableRow) {
      return (
         row.deleted ||
         !this.hasResource(Resources.CTCloseRow) ||
         (row.rowStatus === ChaosTableStatus.OPEN &&
            row.studentStatus !== ChaosTableStudentStatus.NONE)
      );
   }

   joinRowDisabled(row: ChaosTableRow): boolean {
      const isSuccessfulSynced = row.rowStatus === ChaosTableStatus.SUCCESSFUL;

      if (!this.hasResource(Resources.CTJoinItems)) {
         return true;
      }
      if (isSuccessfulSynced) {
         return false;
      }
      return !!row.deleted;
   }

   openEmailTemplateSendDialog(row: ChaosTableRow, type: 'enrollment' | 'reminder') {
      this.dialog.closeAll();

      let title = '';
      let languageSuffix = '';

      if (type === 'enrollment') {
         title = 'Send link for enrollment';
         if (row.language === LanguageEnum.HU) {
            languageSuffix = 'Hungarian';
         } else {
            languageSuffix = 'English';
         }
      } else if (type === 'reminder') {
         title = 'Send reminder link';
         if (row.language === LanguageEnum.HU) {
            languageSuffix = 'Hungarian';
         } else {
            languageSuffix = 'English';
         }
      }

      const dialogRef = this.dialog.open(EmailTemplateSendComponent, {
         width: '400px',
         data: {
            title: title,
            languageSuffix: languageSuffix,
            language: row.language
         }
      });

      dialogRef.afterClosed().subscribe({
         next: result => {
            if (result) {
               if (type === 'enrollment') {
                  this.sendEnrollmentLink({
                     id: row.id,
                     comment: result.comment
                  });
               } else if (type === 'reminder') {
                  this.sendReminderLink({
                     id: row.id,
                     comment: result.comment
                  });
               }
            }
         }
      });
   }

   getAreaName(areaCode: string): string {
      const area = this.areas.find(a => a.code === areaCode);
      return area ? area.value : areaCode;
   }

   isSuccess(row: ChaosTableRow): boolean {
      return row.rowStatus === ChaosTableStatus.SUCCESSFUL;
   }

   isUnsuccessful(row: ChaosTableRow): boolean {
      return row.rowStatus === ChaosTableStatus.UNSUCCESSFUL;
   }

   isOpenAndNotNone(row: ChaosTableRow): boolean {
      return (
         row.rowStatus === ChaosTableStatus.OPEN &&
         row.studentStatus !== ChaosTableStudentStatus.NONE
      );
   }
}

export function getStatus(status: ChaosTableStudentStatus): string {
   switch (status) {
      case ChaosTableStudentStatus.REGISTERED:
      case ChaosTableStudentStatus.WAITING_FOR_PAYMENT:
      case ChaosTableStudentStatus.WAITING_FOR_IPN:
         return 'PAYMENT_PROBLEM';

      default:
         return status;
   }
}
